import { Row } from '@upsales/components';
import { Feature, isAvailable, hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import React, { useEffect, useMemo, useState } from 'react';
import AccountListContacts from 'Components/Account/AccountListContacts/AccountListContacts';
import AccountListBoardOfDirectors from 'Components/Account/AccountListContacts/AccountListBoardOfDirectors';
import BemClass from '@upsales/components/Utils/bemClass';
import './Contacts.scss';
import type Client_ from 'App/resources/Model/Client';
import RequestBuilder from 'Resources/RequestBuilder';
import ContactResource from 'App/resources/Contact';
import type Contact_ from 'App/resources/Model/Contact';
import ContactAttributes from 'App/babel/attributes/Contact';
import logError from 'Helpers/logError';
import ComparisonTypes from 'Resources/ComparisonTypes';
import _ from 'lodash';
import { goToContactCard } from 'App/helpers/contactCardNavigation';
import { Detached as ContactsTab } from 'App/pages/ContactsTab';
import type { AccountCard } from 'App/pages/ClientCard/ClientCard';
import { openEditContact } from 'App/components/EditContact/openEditContact';
import AppService from 'App/legacy/AppService';

type Client = Pick<
	Client_,
	| 'id'
	| 'name'
	| 'journeyStep'
	| 'phone'
	| 'users'
	| 'active'
	| 'createRights'
	| 'prospectingId'
	| 'prospecting'
	| 'description'
	| 'orgNo'
	| 'operationalAccount'
	| 'numberOfSubaccounts'
>;

type Contact = {
	relatedDescription?: string;
	connectedClients?: Client[];
	relatedAccount?: Contact['client'];
	titleCategory?: {
		value?: string;
	};
	score?: number;
} & Contact_;

type Delimiter = {
	delimiter: true;
	name: string;
};

const SimpleClientCardContacts = ({ client, subAccountIds = [] }: { client: Client; subAccountIds?: number[] }) => {
	const accountId = client.id;

	const [sortByRelevance, setSortByRelevance] = useState(true);
	const [showInactive, setShowInactive] = useState(false);
	const [allContacts, setAllContacts] = useState<Contact[]>([]);
	const [searchStr, setSearchStr] = useState('');
	const [loading, setLoading] = useState(true);
	const [sortColumn, setSortColumn] = useState('name');
	const [sortAsc, setSortAsc] = useState(true);

	const shownContacts = useMemo(() => {
		const hasContactTitleCategoryAccess = hasSoftDeployAccess('NEW_FIELDS');
		const search = searchStr.toLowerCase();
		const contacts = allContacts.filter(contact => {
			const nameCheck = contact.name.toLowerCase().includes(search);
			const titleCategoryCheck =
				hasContactTitleCategoryAccess && contact.titleCategory?.value?.toLowerCase().includes(search);
			const titleCheck = contact.title?.toLowerCase().includes(search);
			return (
				(showInactive || (!showInactive && contact.active)) &&
				(!search || (search.length && (nameCheck || titleCategoryCheck || titleCheck)))
			);
		});

		contacts.forEach(contact => {
			if (!contact.client.id || contact.client.id === accountId) {
				return;
			}
			const relatedClient = _.find(contact.connectedClients ?? [], { relatedToClientId: accountId });
			contact.relatedDescription = relatedClient?.description || '';
			contact.relatedAccount = contact.client;
		});

		const finalContacts: (Contact | Delimiter)[] = sortByRelevance
			? contacts
			: (() => {
					contacts.sort((a, b) => {
						if (a.isPriority && !b.isPriority) return -1;
						if (!a.isPriority && b.isPriority) return 1;

						const dir = sortAsc ? 1 : -1;
						let aVal = '';
						let bVal = '';

						switch (sortColumn) {
							case 'score':
								return ((a.score || 0) - (b.score || 0)) * dir;
							case 'actualphone':
								aVal = a.phone || '';
								bVal = b.phone || '';
								break;
							case 'cellphone':
								aVal = a.cellPhone || '';
								bVal = b.cellPhone || '';
								break;
							case 'email':
								aVal = a.email || '';
								bVal = b.email || '';
								break;
							case 'name':
							default:
								aVal = a.name || '';
								bVal = b.name || '';
								break;
						}

						const aStr = String(aVal).toLowerCase();
						const bStr = String(bVal).toLowerCase();
						if (aStr < bStr) return -1 * dir;
						if (aStr > bStr) return 1 * dir;
						return 0;
					});

					if (sortColumn === 'name') {
						const delimContacts: (Contact | Delimiter)[] = [];
						let lastDelim: string | undefined = undefined;
						contacts.forEach(contact => {
							const name = contact.name?.toLowerCase();
							if (name && lastDelim !== name[0]) {
								const delim = name[0];
								delimContacts.push({ delimiter: true, name: delim });
								lastDelim = delim;
							}
							delimContacts.push(contact);
						});
						return delimContacts;
					}

					return contacts;
			  })();
		return finalContacts;
	}, [allContacts, sortByRelevance, showInactive, searchStr, sortColumn, sortAsc]);

	const getContacts = async () => {
		const CHUNK_SIZE = 1000;

		setLoading(true);
		setAllContacts([]);

		// CONSIDER metadata should possibly already be calculated from header
		getContactPromise({ offset: 0, limit: 0 })
			.then(res => {
				const total = res.metadata.total;
				const chunkAmount = Math.ceil(total / CHUNK_SIZE);
				const promises = Array(chunkAmount)
					.fill(undefined)
					.map((_, i) => getContactPromise({ offset: i * CHUNK_SIZE, limit: CHUNK_SIZE }));
				return Promise.all(promises);
			})
			.then(res => {
				setAllContacts(res.map(obj => obj.data).flat());
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const updateIfRelevant = (e: unknown, contact: Contact) => {
		if (contact.client && (contact.client.id === accountId || subAccountIds?.includes(contact.client.id))) {
			getContacts();
		}
	};

	useEffect(() => {
		getContacts();
		const listeners = [
			Tools.$rootScope.$on('contact.added', updateIfRelevant),
			Tools.$rootScope.$on('contact.updated', updateIfRelevant),
			Tools.$rootScope.$on('contact.deleted', getContacts),
			Tools.$rootScope.$on('account.merged', function (e, res) {
				if (res.merged.id === accountId) {
					getContacts();
				}
			})
		];
		return () => listeners.forEach(unsub => unsub());
	}, []);

	useEffect(() => {
		getContacts();
	}, [sortByRelevance]);

	function getContactPromise(opts: { limit: number; offset: number }): Promise<any> {
		const hasAI = isAvailable(Feature.AI);
		if (sortByRelevance) {
			const options = {
				limit: opts.limit,
				offset: opts.offset,
				accountId: accountId,
				subAccountIds,
				includeSignals: hasAI
			};

			return ContactResource.findByRelevance(options);
		} else {
			const filter = new RequestBuilder();
			const or = filter.orBuilder();
			or.next();
			or.addFilter(ContactAttributes.account, ComparisonTypes.Equals, [accountId, ...(subAccountIds ?? [])]);
			or.next();
			or.addFilter(ContactAttributes.connectedClients, ComparisonTypes.Equals, [
				accountId,
				...(subAccountIds ?? [])
			]);
			or.done();
			filter.addSort(ContactAttributes.name, true);
			filter.addSort(ContactAttributes.active, false);
			filter.limit = opts.limit;
			filter.offset = opts.offset;
			const sorts = filter.build().sort || [];
			sorts.unshift({ a: 'isPriority', s: 'Z' });
			return ContactResource.find({
				...filter.build(),
				sort: sorts,
				includeSignals: hasAI
			});
		}
	}

	const actions = {
		editContact: (contact: Contact) => openEditContact({ id: contact.id }),
		removeContact: (contact: Contact) => {
			try {
				ContactResource.delete(contact.id, undefined, contact);
			} catch (err) {
				logError(err, `[AccountListContacts] failed to remove the contact with id ${contact.id}`);
			}
		},
		openContact: (contactId: number) => goToContactCard(contactId),
		toogleSearchBy: (relevance: boolean) => {
			setSortByRelevance(() => relevance);
			if (relevance) {
				setSortColumn('name');
				setSortAsc(true);
			}
		},
		onSort: (column: string) => {
			if (sortColumn === column) {
				setSortAsc(prev => !prev);
			} else {
				setSortColumn(column);
				setSortAsc(true);
			}
			setSortByRelevance(false);
		},
		toogleshowInactiveContacts: (val: boolean) => {
			setShowInactive(() => val);
		},
		searchChanged: (str: string) => {
			setSearchStr(() => str);
		}
	};

	const hasProspectingPro = isAvailable(Feature.PROSPECTING_PRO);
	const hasProspectingBasic = isAvailable(Feature.PROSPECTING_BASIC);
	const canUpgradeToPro =
		hasProspectingBasic &&
		AppService.getAccountSelf()?.version === 'Starter' &&
		!!AppService.getSelf().billingAdmin;
	const showDirectors =
		(hasProspectingPro || canUpgradeToPro) && client?.prospecting?.directors && client.prospectingId;

	return (
		<Row className={new BemClass('ClientCardContent').elem('Contacts').b()}>
			<AccountListContacts
				actions={actions}
				account={client}
				accountId={accountId}
				searchStr={searchStr}
				tableLoading={loading}
				searchByRelevance={sortByRelevance}
				showInactiveContacts={showInactive}
				accountContacts={shownContacts}
				subAccountIds={subAccountIds}
				sortColumn={sortColumn}
				sortAsc={sortAsc}
			/>
			{showDirectors ? (
				<AccountListBoardOfDirectors
					directors={client.prospecting?.directors}
					contact={ContactResource}
					accountId={accountId}
					accountProspectingId={client.prospectingId}
					accountName={client.name}
				/>
			) : null}
		</Row>
	);
};

const ClientCardContacts = ({
	client,
	subAccountIds = [],
	accountCardData
}: {
	client: Client;
	subAccountIds?: number[];
	accountCardData?: AccountCard;
}) => {
	const hasSubAccountsV2 = hasSoftDeployAccess('SUB_ACCOUNTS_V2');
	const hasSubAccounts = isAvailable(Feature.SUB_ACCOUNTS);
	const hasSubaccountFeature = hasSubAccountsV2 && hasSubAccounts;

	if (hasSubaccountFeature && subAccountIds.length > 0) {
		const clientIds = [client.id, ...subAccountIds];
		const numberOfContacts =
			(accountCardData?.activeContacts?.metadata?.total ?? 0) +
			(accountCardData?.inactiveContacts?.metadata?.total ?? 0);
		return (
			<ContactsTab
				clientIds={clientIds}
				orgNumbers={client.orgNo ? [client.orgNo] : []}
				numberOfContacts={numberOfContacts}
				prospectingId={client.prospectingId ?? undefined}
				client={client}
			/>
		);
	}

	return <SimpleClientCardContacts client={client} subAccountIds={subAccountIds} />;
};

export default ClientCardContacts;
